import {
    MarkupTextPositionType,
    MarkupType,
    type ILayerProps,
    type IMarkupLayerProps,
    type IPoint,
} from '@/version-types';
import LabelKit from './label-kit';
import type { IMarkupLabelLayout } from './label-layout';
import { clampMarkupLabelRoundWeight } from './round-weight';

function getArrowLabelCenter(layer: ILayerProps, labelHeight: number): IPoint {
    const { width } = layer;
    const { label, centerOffset } = layer.data as IMarkupLayerProps;
    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);

    if (label.position === MarkupTextPositionType.Down) {
        return {
            x: centerOffset.x + width / 2,
            y: centerOffset.y + labelRenderInfo.renderPosition + labelHeight / 2,
        };
    }
    if (label.position === MarkupTextPositionType.Up) {
        return {
            x: centerOffset.x + width / 2,
            y: centerOffset.y - labelRenderInfo.renderPosition - labelHeight / 2,
        };
    }
    return {
        x: centerOffset.x + width / 2,
        y: centerOffset.y,
    };
}

function getSegmentLabelCenter(layer: ILayerProps, labelHeight: number): IPoint {
    const { label, anchor, centerOffset } = layer.data as IMarkupLayerProps;
    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);
    const labelAnchor = anchor.points![1] || { x: 0, y: 0 };
    const base = {
        x: centerOffset.x + labelAnchor.x,
        y: centerOffset.y + labelAnchor.y,
    };

    if (label.position === MarkupTextPositionType.Center) {
        return {
            x:
                base.x +
                (labelAnchor.x < 0
                    ? -(labelRenderInfo.renderWidth / 2 + labelRenderInfo.marginH)
                    : labelRenderInfo.renderWidth / 2 + labelRenderInfo.marginH),
            y: base.y,
        };
    }
    if (label.position === MarkupTextPositionType.Down) {
        return {
            x: base.x,
            y: base.y + labelRenderInfo.renderPosition + labelHeight / 2,
        };
    }
    return {
        x: base.x,
        y: base.y - labelRenderInfo.renderPosition - labelHeight / 2,
    };
}

function drawRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) {
    const safeRadius = Math.min(Math.max(radius, 0), width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + safeRadius, y);
    ctx.arcTo(x + width, y, x + width, y + height, safeRadius);
    ctx.arcTo(x + width, y + height, x, y + height, safeRadius);
    ctx.arcTo(x, y + height, x, y, safeRadius);
    ctx.arcTo(x, y, x + width, y, safeRadius);
    ctx.closePath();
}

function getLabelFontMetrics(ctx: CanvasRenderingContext2D, fontSize: number) {
    const metrics = ctx.measureText('Mg');
    const ascent = metrics.fontBoundingBoxAscent || metrics.actualBoundingBoxAscent || Math.max(0, fontSize * 0.8);
    const descent = metrics.fontBoundingBoxDescent || metrics.actualBoundingBoxDescent || Math.max(0, fontSize * 0.2);
    return { ascent, descent };
}

export function drawMarkupLabelToCanvas(
    ctx: CanvasRenderingContext2D,
    layer: ILayerProps,
    layout: IMarkupLabelLayout | null,
) {
    if (!layout) return;

    const { markupType, label } = layer.data as IMarkupLayerProps;
    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);
    const labelWidth = Math.ceil(labelRenderInfo.labelWidth);
    const labelHeight = Math.ceil(labelRenderInfo.labelHeight);
    if (!labelWidth || !labelHeight) return;

    const center =
        markupType === MarkupType.Segment
            ? getSegmentLabelCenter(layer, labelHeight)
            : getArrowLabelCenter(layer, labelHeight);

    ctx.save();
    ctx.translate(center.x, center.y);
    if (label.rotate) ctx.rotate((label.rotate * Math.PI) / 180);

    if (label.background) {
        const roundWeight = clampMarkupLabelRoundWeight(label.roundWeight, labelHeight);
        drawRoundRect(ctx, -labelWidth / 2, -labelHeight / 2, labelWidth, labelHeight, roundWeight);
        ctx.fillStyle = label.background;
        ctx.fill();
    }

    ctx.font = layout.font;
    ctx.fillStyle = label.color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const { ascent, descent } = getLabelFontMetrics(ctx, layout.fontSize);
    const halfLeading = (layout.lineHeight - ascent - descent) / 2;
    const startX = -layout.width / 2;
    const startY = -layout.height / 2;

    layout.lines.forEach((line, index) => {
        if (!line.text) return;
        const baselineY = startY + index * layout.lineHeight + halfLeading + ascent;
        if (!layout.letterSpacing) {
            ctx.fillText(line.text, startX, baselineY);
            return;
        }

        let x = startX;
        line.graphemes.forEach((grapheme, graphemeIndex) => {
            ctx.fillText(grapheme.text, x, baselineY);
            x += grapheme.width;
            if (graphemeIndex < line.graphemes.length - 1) x += layout.letterSpacing;
        });
    });
    ctx.restore();
}
